<i18n lang='yml' src='../i18n/Objectives.yml'></i18n>

<template>
    <div class='objectives-vue'>
        <fieldset class='align-browse border-bottom' v-if='edit'>
            <legend class='heading-medium'>{{ $t('select') }}</legend>
            <div class='filter-bar'>
                <div role='toolbar' class='btn-toolbar'>

                    <div class='btn-group btn-group-sm dropdown-select network-select' role="group" :aria-label="$t('network_select')">
                        <button class='btn btn-outline-primary dropdown-toggle'
                            data-toggle='dropdown' :data-network-id='network.selectedNetwork.id'>
                            {{ truncate(network.selectedNetwork.name || $t('defaultNetwork')) }}
                            <font-awesome-icon :icon="['fa', 'caret-down']"/>
                        </button>
                        <ul role='menu' class='dropdown-menu'>
                            <li v-for='option in network.options' :key='option.id' v-bind:class='{ "active": option.id === network.selectedNetwork.id }'>
                                <a v-on:click='setSelectedNetwork(option.id)'>
                                    {{ option.name }}
                                </a>
                            </li>
                        </ul>
                    </div>


                    <template v-for='(hierarchy, level) in classificationHierarchies' :key='level'>
                        <div class='btn-group btn-group-sm'  role="group" :aria-label="$t('separator_element')">
                            <span class='separator'>
                                <font-awesome-icon :icon="['fa', 'chevron-right']"/>
                            </span>
                        </div>

                        <div class='btn-group btn-group-sm dropdown-select classification-select' role="group" :aria-label="$t('classification_select')">
                            <a class='btn btn-primary dropdown-toggle' data-toggle='dropdown'>
                                {{ truncate(hierarchy.selectedItem ? hierarchy.selectedItem.system_classification.name : $t('defaultSelect')) }}
                                 <font-awesome-icon :icon="['fa', 'caret-down']"/>
                            </a>
                            <ul role='menu' class='dropdown-menu'>
                                <li :key='option.id' v-for='option in hierarchy.items' v-bind:class='{ "active": hierarchy.selectedItem && (option.id === hierarchy.selectedItem.id) }'>
                                    <a v-on:click='selectClassificationHierarchy(level, option.id)'>
                                        {{ option.system_classification.name }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </template>
                </div>
            </div>


            <div class='onboard onboard-sm no-margin no-content' v-show='!selectedHierarchy || selectedHierarchy.objectives.length < 1'>
                <div class='onboard-wrap'>
                    <font-awesome-icon :icon="['fas', 'list']" aria-hidden='true'/>
                    <h5>{{ $t('noSelections') }}</h5>
                    <p>{{ $t('browseObjectives') }}</p>
                </div>
            </div>
            <ul class='list-unstyled layout-block-list block-links wrap border-all' v-if='selectedHierarchy'>
                <li v-for='obj in selectedHierarchy.objectives'
                    v-on:mouseenter='objectiveMouseEnter'
                    v-on:mouseleave='objectiveMouseLeave'
                    v-bind:class='{ "active": isObjectiveSelected(obj.id) }'>
                    <label>
                        <input type='checkbox'
                               :value='obj.id'
                               v-on:click='handleObjectiveClick($event, obj.id)'
                               :checked='isObjectiveSelected(obj.id)'
                               v-bind:class='{ "active": isObjectiveSelected(obj.id) }'>
                        <i class='fa fa-fw'
                           aria-hidden='true'
                           v-bind:class='{ "fa-check-square": isObjectiveSelected(obj.id), "fa-square-o": !isObjectiveSelected(obj.id) }'></i>
                        <strong>{{ obj.identifier }}</strong>
                        <strong v-html='obj.alignment_text'></strong>
                    </label>
                </li>
            </ul>
        </fieldset>


        <fieldset>
            <legend class='heading-medium'>{{ $t('objectives') }}
                <a class='btn-show-text' v-on:click='showTextClick'>
                    {{ $t('toggleText') }}
                </a>
            </legend>
            <div class='onboard onboard-sm no-margin no-content' v-show='Object.keys(selectedObjectives).length === 0'>
                <div class='onboard-wrap'>
                    <font-awesome-icon :icon="['fas', 'list']" aria-hidden='true'/>
                    <h5>{{ $t('noObjectives') }}</h5>
                    <p v-if='edit'>{{ $t('browseObjectives') }}</p>
                </div>
            </div>
            <ul id='aligned-objectives-list' class='list-unstyled layout-block-list block-links list-selected'>
                <template v-for='hierarchy in selectedObjectives'>
                    <li class='full-width'>
                        <p class='path' v-show='Object.keys(hierarchy._objectives).length > 0'>
                            {{ hierarchy._hierarchy.hierarchy.path.replace(/-/g, ' > ') }}
                        </p>
                    </li>
                    <li v-for='obj in hierarchy._objectives'
                        class='active'>
                        <label>
                            <input type='checkbox' class='active' checked
                                   v-on:click='handleObjectiveClick($event, obj.objective.id, hierarchy._hierarchy)'
                                   :value='obj.objective.id' :name='fieldName + "[objective_ids][]"'>
                            <i class='fa fa-fw fa-check-square' aria-hidden='true'></i>
                            <strong>{{ obj.objective.identifier }}</strong>
                            {{ obj.objective.alignment_text }}
                        </label>
                    </li>

                </template>
            </ul>

        </fieldset>

        <input type='hidden' :name='fieldName + "[objective_ids][]"' value=''>
    </div>
</template>

<script>

    /**
     *  the selectedObjectives are in the following format:
     *  {
     *      system_hierarchy_id: {
     *          _hierarchy: {
     *              // THIS IS THE SYSTEM HIERARCHY MODEL
     *          },
     *          _objectives: {
     *              objective_id: {
     *                  hierarchy: {
     *                      // THIS IS THE HIERARCHY MODEL
     *                  },
     *                  objective: {
     *                      // THIS IS THE OBJECTIVE MODEL
     *                  }
     *              },
     *              objective_id: {
     *                  hierarchy: {
     *                      // THIS IS THE HIERARCHY MODEL
     *                  },
     *                  objective: {
     *                      // THIS IS THE OBJECTIVE MODEL
     *                  }
     *              }
 *              }
     *      }
     */
    export default {
        name: 'Objectives',
        props: {
            locale: { type: String },
            endpoint: { type: String },
            edit: { type: Boolean },
            fieldName: { type: String },
            initialObjectives: { type: String, default: '{}'}
        },
        methods: {
            loadNetworks: function() {
                const self = this;
                $.getJSON(self.endpoint, function(response) {
                    self.network.options = response;
                    self.network.selected = {}
                });
            },
            resetSelections: function(level) {
                level = level || 0;
                this.selectedHierarchy = null;
                this.classificationHierarchies.splice(level);

                if(level===0){
                    this.getClassifications();
                }
            },
            setSelectedNetwork: function(networkId) {
                // Do we even need to change anything?
                if(this.network.selectedNetwork && this.network.selectedNetwork.id === networkId) {
                    return;
                }

                // Figure out which network it was
                for(let count in this.network.options) {
                    if(this.network.options[count].id === networkId) {
                        this.network.selectedNetwork = this.network.options[count];
                    }
                }

                // Reset the data
                this.resetSelections();

            },
            getClassifications: function(classificationHierarchy) {
                const self = this;

                let jsonParams = { network: this.network.selectedNetwork.id };
                if(classificationHierarchy) {
                    jsonParams.classification_hierarchy = classificationHierarchy;
                }

                $.getJSON(self.endpoint, jsonParams, function(response) {
                    if(response.length < 1) {
                        return;
                    }
                    const idx = self.classificationHierarchies.length;
                    const item = {
                        items: response,
                        selectedItem: null
                    };
                    self.classificationHierarchies[idx] = item;
                });
            },
            selectClassificationHierarchy: function(level, classificationHierarchyId) {
                this.resetSelections(level+1);

                // Start getting the next level
                this.getClassifications(classificationHierarchyId);

                // Figure out which classificationHierarchy it was so that we can set it as the selected one
                const hierarchyLevel = this.classificationHierarchies[level];
                for(let count = 0; count < hierarchyLevel.items.length; count++) {
                    if(hierarchyLevel.items[count].id === classificationHierarchyId) {
                        hierarchyLevel.selectedItem = hierarchyLevel.items[count];
                        this.selectedHierarchy = hierarchyLevel.selectedItem;
                    }
                }
            },
            handleObjectiveClick: function(e, objectiveId, hierarchy) {
                if (!this.edit) {
                    return;
                }
                hierarchy = hierarchy || this.selectedHierarchy;

                // We need the actual objective
                let selectedObjective;
                for(let count in hierarchy.objectives) {
                    if(hierarchy.objectives[count].id === objectiveId) {
                        selectedObjective = hierarchy.objectives[count];
                    }
                }

                const objective = {
                    objective: selectedObjective,
                    systemHierarchy: hierarchy
                };

                let objectivesLocation = this.selectedObjectives[hierarchy.id];

                // Create the storage area for the selected hierarchy
                if (!objectivesLocation) {
                    this.selectedObjectives[hierarchy.id] = {};

                    objectivesLocation = this.selectedObjectives[hierarchy.id];
                    objectivesLocation._hierarchy = hierarchy;
                    objectivesLocation._objectives = {};
                }

                // Next check if we are adding or deleting
                if (objectivesLocation._objectives[objectiveId]) {
                    const self = this;
                    // TODO: REMOVE THIS FUCKING HACK
                    setTimeout(function() {
                        self.$delete(objectivesLocation._objectives, objectiveId, objective);
                    }, 150);

                } else {
                    objectivesLocation._objectives[objectiveId] = objective;
                }
            },
            isObjectiveSelected: function(objectiveId, hierarchy) {
                hierarchy = hierarchy || this.selectedHierarchy;

                if (this.selectedObjectives[hierarchy.id]) {
                    return this.selectedObjectives[hierarchy.id]._objectives[objectiveId] ? true : false;
                } else {
                    return false;
                }
            },
            objectiveMouseEnter: function(e) {
                const target = $(e.currentTarget);
                if (target.hasClass('active')) {
                    target.find('i').removeClass('fa-check-square').addClass('fa-minus-square red');
                } else {
                    target.find('i').removeClass('fa-square-o').addClass('fa-check-square');
                }
            },
            objectiveMouseLeave: function(e) {
                const target = $(e.currentTarget);
                if (target.hasClass('active')) {
                    target.find('i').removeClass('fa-minus-square red').addClass('fa-check-square');
                } else {
                    target.find('i').removeClass('fa-check-square').addClass('fa-square-o');
                }
            },
            showTextClick: function(e) {
                e.preventDefault();
                this.addEllipsis = !this.addEllipsis;
            },
            truncate: function(text, maxLength = 30) {
                let result = text.trim();
                if (result.length > maxLength) {
                    result = result.substr(0, maxLength);
                    result += '...';
                }
                return result;
            }
        },
        data () {
            let selectedObjectives = {};
            let start = JSON.parse(this.initialObjectives);

            for(let o in start) {
                let objective = start[o];

                if(!selectedObjectives[objective.system_classification_hierarchy.id]) {
                    selectedObjectives[objective.system_classification_hierarchy.id] = {
                        _hierarchy: objective.system_classification_hierarchy
                    };
                }

                const loc = selectedObjectives[objective.system_classification_hierarchy.id];
                loc._objectives = loc._objectives || {};
                loc._objectives[objective.id] = {
                    objective: objective,
                    hierarchy: objective.system_classification_hierarchy
                };
            }

            return {
                network: {
                    options: [],
                    selectedNetwork: {}
                },
                classificationHierarchies: [],
                selectedHierarchy: null,
                selectedObjectives: selectedObjectives,
                addEllipsis: true
            };
        },
        mounted () {
            this.$i18n.locale = this.locale;
            this.loadNetworks();
        },
        computed: {
        }
    }
</script>
